// src/components/admin/diagnostics/diagnosticReportsData.js
//
// Shared data helpers for the unified Diagnostic Reports page.
// Reads the same `diagnostic_results` collection the individual SAT / PSAT / ACT
// report viewers read — this module only changes how attempts are *listed*, not
// how the full per-attempt reports are rendered.
//
// DO NOT add report-rendering logic here — display-agnostic data only.

import { db } from "../../../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";

// ── Type normalization ──────────────────────────────────────────────────────
// Legacy SAT attempts may have no `type` field; treat a missing type as "SAT".
export function normalizeTestType(rawType) {
  const t = String(rawType || "").trim().toUpperCase();
  if (t === "PSAT") return "PSAT";
  if (t === "ACT") return "ACT";
  return "SAT";
}

export function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateShort(v) {
  const d = toDateSafe(v);
  if (!d) return "—";
  return d.toLocaleDateString();
}

function completionOf(data) {
  return data?.completedAt || data?.completed_on || data?.completedAtIso || null;
}

function isCompletedAttempt(data) {
  return data?.status === "completed" || !!data?.completedAt;
}

// ── Score display ─────────────────────────────────────────────────────────────
// Each test type stores its headline score under different field names. We show
// the best stored value for at-a-glance scanning; the full report (opened on
// row click) computes anything more involved (e.g. PSAT scaling tables).
export function formatScore(type, data) {
  if (type === "ACT") {
    const composite = data?.compositeScore ?? data?.composite ?? data?.actComposite ?? data?.score;
    return composite == null ? "—" : `${composite} Composite`;
  }
  if (type === "PSAT") {
    const total =
      data?.psatTotalScaled ?? data?.totalScaled ?? data?.totalScore ?? data?.totalScaledScore ?? data?.score;
    return total == null ? "—" : `${total}`;
  }
  // SAT
  const total = data?.totalScore ?? data?.totalScaled ?? data?.totalScaledScore ?? data?.score;
  return total == null ? "—" : `${total}`;
}

export function attemptTitle(type, data) {
  return (
    data?.testTitle ||
    data?.testName ||
    data?.displayName ||
    (data?.testId ? `${type} Diagnostic (${data.testId})` : `${type} Diagnostic`)
  );
}

// ── Load students who have diagnostic attempts in the requested set ───────────
// `archived`: undefined → any, false → only students with active reports,
// true → only students with archived reports.
// Returns [{ uid, name, email, lastCompletedAt, lastDateLabel }] sorted by most
// recent completion, matching the shape SearchableStudentDropdown expects.
export async function fetchAllDiagnosticStudents({ archived } = {}) {
  const snapshot = await getDocs(collection(db, "diagnostic_results"));

  // studentId -> most recent matching completion millis
  const studentMap = new Map();
  snapshot.forEach((docSnap) => {
    const d = docSnap.data() || {};
    const sid = d.studentId;
    if (!sid) return;
    if (!isCompletedAttempt(d)) return;
    if (archived === true && !d.isArchived) return;
    if (archived === false && !!d.isArchived) return;
    const dateObj = toDateSafe(completionOf(d));
    const timeVal = dateObj ? dateObj.getTime() : 0;
    if (!studentMap.has(sid) || timeVal > studentMap.get(sid)) {
      studentMap.set(sid, timeVal);
    }
  });

  const uniqueIds = Array.from(studentMap.keys());
  if (uniqueIds.length === 0) return [];

  const loaded = await Promise.all(
    uniqueIds.map(async (uid) => {
      const lastMs = studentMap.get(uid) || 0;
      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        return {
          uid,
          name: userData.name || "Unknown Name",
          email: userData.email || "",
          lastCompletedAt: lastMs,
          lastDateLabel: lastMs > 0 ? new Date(lastMs).toLocaleDateString() : "N/A",
        };
      } catch (err) {
        console.error("Error fetching user", uid, err);
        return { uid, name: "Unknown (Error)", email: "", lastCompletedAt: lastMs, lastDateLabel: "N/A" };
      }
    })
  );

  loaded.sort((a, b) => (b.lastCompletedAt || 0) - (a.lastCompletedAt || 0));
  return loaded;
}

// ── Load every diagnostic attempt for one student (all types, incl. archived) ──
// Returns normalized rows for the unified table, sorted newest first.
export async function fetchAllAttemptsForStudent(studentId) {
  if (!studentId) return [];
  const q = query(collection(db, "diagnostic_results"), where("studentId", "==", studentId));
  const snap = await getDocs(q);

  const rows = [];
  snap.forEach((d) => {
    const data = d.data() || {};
    if (!isCompletedAttempt(data)) return;
    const type = normalizeTestType(data.type);
    const completed = completionOf(data);
    rows.push({
      attemptId: d.id,
      studentId,
      type,
      testId: data.testId || "",
      title: attemptTitle(type, data),
      completedAt: completed,
      completedLabel: formatDateShort(completed),
      completedMs: toDateSafe(completed)?.getTime() ?? 0,
      score: formatScore(type, data),
      status: data.status || (data.completedAt ? "completed" : "—"),
      isArchived: !!data.isArchived,
    });
  });

  rows.sort((a, b) => (b.completedMs || 0) - (a.completedMs || 0));
  return rows;
}

// Canonical ordering for test-type pills (SAT, then PSAT, then ACT).
const TYPE_ORDER = { SAT: 0, PSAT: 1, ACT: 2 };

// ── Load EVERY diagnostic attempt across all students (for the log-style view) ──
// Single scan of `diagnostic_results`, joined with `users`, returns both:
//   • students: [{ uid, name, email, lastCompletedAt, lastDateLabel, testTypes }]
//       — `testTypes` is the sorted set of types that student has (e.g. ["SAT","ACT"]),
//         used to render test-type pills in the searchable dropdown.
//   • attempts: [{ ...attempt row, studentName, studentEmail }] sorted newest first,
//       — the full report history shown in the table by default.
// `archived`: false → active only, true → archived only, undefined → all.
// Cost matches the old fetchAllDiagnosticStudents (same single scan + user lookups).
export async function fetchAllDiagnosticData({ archived } = {}) {
  const snapshot = await getDocs(collection(db, "diagnostic_results"));

  const rawAttempts = [];
  const studentMap = new Map(); // sid -> { lastMs, types:Set }

  snapshot.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const sid = data.studentId;
    if (!sid) return;
    if (!isCompletedAttempt(data)) return;
    if (archived === true && !data.isArchived) return;
    if (archived === false && !!data.isArchived) return;

    const type = normalizeTestType(data.type);
    const completed = completionOf(data);
    const dateObj = toDateSafe(completed);
    const ms = dateObj ? dateObj.getTime() : 0;

    rawAttempts.push({
      attemptId: docSnap.id,
      studentId: sid,
      type,
      testId: data.testId || "",
      title: attemptTitle(type, data),
      completedAt: completed,
      completedLabel: formatDateShort(completed),
      completedMs: ms,
      score: formatScore(type, data),
      status: data.status || (data.completedAt ? "completed" : "—"),
      isArchived: !!data.isArchived,
    });

    let entry = studentMap.get(sid);
    if (!entry) {
      entry = { lastMs: 0, types: new Set(), count: 0 };
      studentMap.set(sid, entry);
    }
    entry.types.add(type);
    entry.count += 1;
    if (ms > entry.lastMs) entry.lastMs = ms;
  });

  const uniqueIds = Array.from(studentMap.keys());
  if (uniqueIds.length === 0) return { students: [], attempts: [] };

  const userEntries = await Promise.all(
    uniqueIds.map(async (uid) => {
      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        const u = userSnap.exists() ? userSnap.data() : {};
        return [uid, { name: u.name || "Unknown Name", email: u.email || "" }];
      } catch (err) {
        console.error("Error fetching user", uid, err);
        return [uid, { name: "Unknown (Error)", email: "" }];
      }
    })
  );
  const userById = new Map(userEntries);

  const students = uniqueIds
    .map((uid) => {
      const entry = studentMap.get(uid);
      const u = userById.get(uid) || {};
      const testTypes = Array.from(entry.types).sort(
        (a, b) => (TYPE_ORDER[a] ?? 9) - (TYPE_ORDER[b] ?? 9)
      );
      return {
        uid,
        name: u.name || "Unknown Name",
        email: u.email || "",
        lastCompletedAt: entry.lastMs,
        lastDateLabel: entry.lastMs > 0 ? new Date(entry.lastMs).toLocaleDateString() : "N/A",
        testTypes,
        reportCount: entry.count,
      };
    })
    .sort((a, b) => (b.lastCompletedAt || 0) - (a.lastCompletedAt || 0));

  const attempts = rawAttempts
    .map((a) => {
      const u = userById.get(a.studentId) || {};
      return { ...a, studentName: u.name || "Unknown Name", studentEmail: u.email || "" };
    })
    .sort((a, b) => (b.completedMs || 0) - (a.completedMs || 0));

  return { students, attempts };
}

// ── Report URL routing ────────────────────────────────────────────────────────
// Maps an attempt to the shared standalone report route, which opens the full
// report in a new tab with NO portal chrome (no admin sidebar). Route shape:
//   <prefix>/diagnostics/report/:type/:variant?studentId=…&attemptId=…
// PSAT has no archive viewer (PSAT attempts can't be archived through the UI), so
// it always opens as "active".
//
// prefix="" → Admin portal; prefix="/tutor" → Tutor portal. Both resolve to the
// same StandaloneDiagnosticReport component (see App.js routes).
// ── Permanent deletion of a single diagnostic attempt ─────────────────────────
// Removes ALL data tied to one student's attempt of one test so the diagnostic
// can be cleanly assigned/taken again:
//   • diagnostic_results/{attemptId}            — the completed result/report
//   • student_responses where studentId+testId  — per-module answer snapshots
//   • student_test_access/{studentId_testId}     — the access/finished record
//   • diagnostic_live_progress/{studentId_testId}— any live-progress summary
//
// attemptId is `${studentId}_${testId}` for SAT/PSAT/ACT, which is also the id
// used by student_test_access and diagnostic_live_progress. We query
// student_responses by studentId only (single-field, no composite index needed)
// and filter testId in memory.
export async function deleteDiagnosticAttempt(attempt) {
  const studentId = attempt?.studentId;
  const testId = attempt?.testId || "";
  const attemptId = attempt?.attemptId;
  if (!studentId || !attemptId) {
    throw new Error("Missing studentId/attemptId for deletion.");
  }

  const batch = writeBatch(db);

  // 1) The completed result / report document.
  batch.delete(doc(db, "diagnostic_results", attemptId));

  // 2) Access + live-progress records (same composite id).
  batch.delete(doc(db, "student_test_access", attemptId));
  batch.delete(doc(db, "diagnostic_live_progress", attemptId));

  // 3) Per-module response snapshots for this test.
  if (testId) {
    const respSnap = await getDocs(
      query(collection(db, "student_responses"), where("studentId", "==", studentId))
    );
    respSnap.forEach((d) => {
      const data = d.data() || {};
      if ((data.testId || "") === testId) batch.delete(d.ref);
    });
  }

  await batch.commit();
  return { attemptId, studentId, testId };
}

export function buildDiagnosticReportUrl(attempt, { prefix = "" } = {}) {
  if (!attempt?.studentId || !attempt?.attemptId) return null;
  const type = String(attempt.type || "SAT").toLowerCase();
  const variant = attempt.isArchived && type !== "psat" ? "archived" : "active";
  const params = new URLSearchParams({
    studentId: attempt.studentId,
    attemptId: attempt.attemptId,
  });
  return `${prefix}/diagnostics/report/${type}/${variant}?${params.toString()}`;
}
